import React, { useState } from 'react';

import qs from 'qs';

import {
    Grid,
    Box,
    P,
    H2,
    FieldText,
} from '@saladbob/sassafras';


import getSite from '../../../crud/getSite';
import getHostBySlug from '../../../crud/getHostBySlug';
import Blocks from '../../../components/blocks/Blocks';
import Layout from '../../../components/Layout';
import HostLayout from '../../../components/hosts/Layout';
import CourtListing from '../../../components/brackets/CourtListing';
import Winners from '../../../components/brackets/Winners';

// Crud
import { publishedByTournament, publishedByTournamentFinished } from '../../../crud/gql/Brackets';
import fetchGql from '../../../crud/fetchGql';

import type { NextPageWithLayout, PageProps } from '../../_app';

import Host from '../../../types/Host';
import { BracketDepth } from '../../../types/Bracket';

type Props = {
    host: Host,
    bracket?: BracketDepth,
    Header: React.FC,
};

export async function getStaticPaths() {
    return {
        paths: [],
        fallback: 'blocking', // can also be true or 'blocking'
    }
}

export async function getStaticProps({ params }) {
    if (!params.id || params.id === 'undefined') {
        return {
            notFound: true,
        }
    }

    const host = await getHostBySlug(process.env.NEXT_PUBLIC_API_URL, params.id);

    if (!host) {
        return {
            notFound: true,
        }
    }

    if (host.redirect?.trim()) {
        return {
            redirect: {
                destination: host.redirect.trim(),
                permanent: false,
            },
        };
    }

    const site = await getSite();

    const brackets = await fetchGql({
        query: host.activeTournament?.status === 'finished' ? publishedByTournamentFinished : publishedByTournament,
        variables: {
            id: host.activeTournament.id,
        },
    });

    return {
        props: {
            metaTitle: host?.name,
            bracket: brackets.data.Brackets.docs[0] || null,
            host,
            ...site,
        },
    }
}

const Page: NextPageWithLayout<Props> = ({ host, bracket }) => {
    if (host.activeTournament?.status == 'finished') {
        return (<Winners tournament={host?.activeTournament} bracket={bracket} />);
    }
    if (bracket?.courts?.length > 0) {
        return (<CourtListing host={host} courts={bracket.courts} bracketId={bracket.id} />);
    }
    if (!host.layout || host.layout.length === 0) {
        return (
            <Box padding="lg">
                <P font="leader">
                    <strong>Welcome to the Gus Macker 3-on-3 Basketball Tournament in {host.name}!</strong> Part of the legendary 
                    Gus Macker World Tour, this event brings together players of 
                    all ages and skill levels for a weekend of exciting basketball, community spirit, and outdoor fun.
                </P>

                <P font="leader">
                    Gus Macker tournaments have a rich history rooted in grassroots basketball. What began in 1974 as a small driveway 
                    game in Michigan has grown into a nationwide phenomenon that celebrates competition, teamwork, and community.
                </P>

                <P font="leader">
                    At this event, teams compete in fast-paced 3-on-3 games on half-court setups throughout the host city.
                    Whether you’re a seasoned competitor or playing for fun, there’s a division for everyone — from
                    youth and beginner teams to highly competitive adult brackets.
                </P>

                <P font="leader">
                    Beyond the games, the tournament weekend is a festive experience with music, local food vendors,
                    and activities for families and fans. It’s more than just basketball — it’s a celebration of
                    community, passion, and friendly competition.
                </P>
                <P font="leader">
                    Grab your team, bring your friends and family, and be part of the action — we’ll see you on the courts!
                </P>
            </Box>
        )
    }
    return (<Blocks blocks={host.layout} />);
};

Page.getLayout = function getLayout(page: React.ReactElement, pageProps: PageProps & Props ) {
    let currentPage = 'about';
    if (pageProps.host?.activeTournament?.status === 'finished') {
        currentPage = 'winners';
    } else if (pageProps.bracket) {
        currentPage = 'brackets';
    }
    return (
        <Layout { ...pageProps }>
            <HostLayout page={currentPage} brackets={pageProps.bracket ? true : false} {...pageProps}>
                {page}
            </HostLayout>
        </Layout>
    )
}

export default Page;
